
import { useState, useEffect } from "react";
import { LineChart, BarChart, PieChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const TEMPLATES = [
  {
    id: "ceo",
    title: "CEO Dashboard",
    description: "Comprehensive view of company performance across all key metrics",
    metrics: ["Revenue Growth", "Customer Acquisition", "Market Share", "Team Performance"],
    path: "/ceo-dashboard",
    icon: <LineChart className="h-5 w-5" />
  },
  {
    id: "coo",
    title: "COO Dashboard",
    description: "Operational excellence metrics to optimize business processes",
    metrics: ["Operational Efficiency", "Resource Utilization", "Production Metrics", "Quality Control"],
    path: "/coo-dashboard",
    icon: <BarChart className="h-5 w-5" />
  },
  {
    id: "cfo",
    title: "CFO Dashboard",
    description: "Financial insights for strategic decision making and planning",
    metrics: ["Cash Flow", "Profit Margins", "Expense Tracking", "Financial Forecasts"],
    path: "/cfo-dashboard",
    icon: <PieChart className="h-5 w-5" />
  },
  {
    id: "cmo",
    title: "CMO Dashboard",
    description: "Marketing performance and campaign analytics in one place",
    metrics: ["Campaign ROI", "Channel Performance", "Lead Generation", "Conversion Rates"],
    path: "/cmo-dashboard",
    icon: <LineChart className="h-5 w-5" />
  }
];

export function TemplateShowcase() {
  const [activeTemplate, setActiveTemplate] = useState(TEMPLATES[0]);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    const section = document.getElementById("templates");
    if (section) {
      observer.observe(section);
    }
    
    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="templates" className="py-20 bg-gray-50 dark:bg-slate-950 overflow-hidden">
      <div className="container mx-auto px-4 relative">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Dashboard Templates for Every Role
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Start instantly with pre-built dashboards tailored to your specific role.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-center">
          <div className={`w-full lg:w-2/5 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-2xl font-bold mb-2">{activeTemplate.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {activeTemplate.description}
                </p>
                <div className="space-y-3">
                  {activeTemplate.metrics.map((metric, index) => (
                    <div key={index} className="flex items-center">
                      <div className="h-2 w-2 rounded-full bg-primary mr-2"></div>
                      <span className="text-sm font-medium">{metric}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between p-6">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  View the live demo
                </span>
                <Link to={activeTemplate.path}>
                  <Button size="sm">
                    Explore
                  </Button>
                </Link>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setActiveTemplate(template)}
                  className={`p-4 rounded-lg transition-all duration-300 flex items-center ${
                    activeTemplate.id === template.id
                      ? "bg-primary text-white"
                      : "bg-white dark:bg-slate-900 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800"
                  }`}
                >
                  <div className={`mr-3 ${
                    activeTemplate.id === template.id ? "text-white" : "text-primary"
                  }`}>
                    {template.icon}
                  </div>
                  <span className="font-medium">{template.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={`w-full lg:w-3/5 relative transition-all duration-700 transform ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="dashboard-gradient"></div>
            <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-xl relative z-10">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <div className="flex items-center">
                  {activeTemplate.icon}
                  <h3 className="ml-2 font-semibold">{activeTemplate.title}</h3>
                </div>
                <div className="flex space-x-2">
                  <span className="h-3 w-3 bg-red-500 rounded-full"></span>
                  <span className="h-3 w-3 bg-yellow-500 rounded-full"></span>
                  <span className="h-3 w-3 bg-green-500 rounded-full"></span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-6">
                {/* KPI Cards */}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm col-span-1">
                  <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-1">Revenue</h4>
                  <p className="text-2xl font-bold">$1.2M</p>
                  <div className="flex items-center mt-2 text-green-500 text-xs">
                    <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>12.5%</span>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm col-span-1">
                  <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-1">Customers</h4>
                  <p className="text-2xl font-bold">8,642</p>
                  <div className="flex items-center mt-2 text-green-500 text-xs">
                    <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>8.3%</span>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm col-span-1">
                  <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg. Order</h4>
                  <p className="text-2xl font-bold">$142</p>
                  <div className="flex items-center mt-2 text-red-500 text-xs">
                    <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>2.1%</span>
                  </div>
                </div>
                
                {/* Chart Area */}
                <div className="col-span-3 mt-4">
                  <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                    <h4 className="text-sm font-medium mb-4">Revenue Trend</h4>
                    <div className="h-48 relative">
                      {/* Chart placeholder */}
                      <div className="absolute bottom-0 left-0 right-0 flex items-end h-36">
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-100 dark:bg-blue-900/50 rounded-t h-12"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-200 dark:bg-blue-800/50 rounded-t h-16"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-300 dark:bg-blue-700/50 rounded-t h-24"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-400 dark:bg-blue-600/50 rounded-t h-20"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-500 dark:bg-blue-500/50 rounded-t h-28"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-400 dark:bg-blue-600/50 rounded-t h-24"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-500 dark:bg-blue-500/50 rounded-t h-32"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-400 dark:bg-blue-600/50 rounded-t h-28"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-300 dark:bg-blue-700/50 rounded-t h-20"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-200 dark:bg-blue-800/50 rounded-t h-16"></div>
                        </div>
                        <div className="flex-1 mx-1">
                          <div className="bg-blue-100 dark:bg-blue-900/50 rounded-t h-12"></div>
                        </div>
                      </div>
                      
                      {/* Line chart overlay */}
                      <div className="absolute top-0 left-0 right-0 h-full flex items-center">
                        <div className="relative w-full h-px bg-blue-200 dark:bg-blue-700">
                          <div className="absolute -top-1 h-2 w-2 bg-blue-500 rounded-full" style={{ left: '10%' }}></div>
                          <div className="absolute -top-1 h-2 w-2 bg-blue-500 rounded-full" style={{ left: '25%' }}></div>
                          <div className="absolute -top-1 h-2 w-2 bg-blue-500 rounded-full" style={{ left: '40%' }}></div>
                          <div className="absolute -top-1 h-2 w-2 bg-blue-500 rounded-full" style={{ left: '55%' }}></div>
                          <div className="absolute -top-1 h-2 w-2 bg-blue-500 rounded-full" style={{ left: '70%' }}></div>
                          <div className="absolute -top-1 h-2 w-2 bg-blue-500 rounded-full" style={{ left: '85%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Additional metrics */}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm col-span-2">
                  <h4 className="text-sm font-medium mb-3">Traffic Sources</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="h-3 w-3 bg-blue-500 rounded-full mr-2"></span>
                        <span className="text-xs">Organic</span>
                      </div>
                      <span className="text-xs font-medium">42%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="h-3 w-3 bg-indigo-500 rounded-full mr-2"></span>
                        <span className="text-xs">Direct</span>
                      </div>
                      <span className="text-xs font-medium">28%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="h-3 w-3 bg-purple-500 rounded-full mr-2"></span>
                        <span className="text-xs">Referral</span>
                      </div>
                      <span className="text-xs font-medium">18%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="h-3 w-3 bg-pink-500 rounded-full mr-2"></span>
                        <span className="text-xs">Social</span>
                      </div>
                      <span className="text-xs font-medium">12%</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm col-span-1">
                  <h4 className="text-xs text-gray-500 dark:text-gray-400 mb-1">Conversion</h4>
                  <div className="flex items-baseline">
                    <p className="text-2xl font-bold">3.2%</p>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">vs goal: 3%</span>
                  </div>
                  <div className="mt-3 h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '80%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
